import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, authedFetch, resolveImageUrl } from "../lib/api";
import { useAdminAuth } from "../lib/auth";

const Gallery = () => {
  const { isAuthed: isAdmin } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const fetchAll = () =>
    api
      .get("/gallery")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => {
    fetchAll();
  }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const token = localStorage.getItem("swk_admin_token");
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const uploadRes = await authedFetch(`${API_BASE}/admin/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const upData = await uploadRes.json();
        if (!uploadRes.ok) throw new Error(upData.detail || "Upload failed");
        await api.post("/admin/gallery", { url: upData.url });
      }
      toast.success(`Added ${files.length} image${files.length === 1 ? "" : "s"}`);
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this image from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Removed");
      fetchAll();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="section-pad py-16" data-testid="gallery-page">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <h1 className="font-blackletter" style={{ fontSize: "clamp(4rem, 12vw, 9rem)" }}>
          gallery
        </h1>
        {isAdmin && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => uploadFiles(e.target.files)}
              data-testid="gallery-upload-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              data-testid="gallery-upload-btn"
            >
              {uploading ? (
                <><Loader2 size={14} className="animate-spin" /> Uploading…</>
              ) : (
                <><Plus size={14} strokeWidth={2} /> Upload</>
              )}
            </button>
          </>
        )}
      </div>

      {loading ? null : items.length === 0 ? (
        <div className="text-sm text-black/40 py-16 text-center">No images yet.</div>
      ) : (
        <div className="gallery-masonry" data-testid="gallery-grid">
          {items.map((it) => (
            <div key={it.id} className="relative group gallery-tile" data-testid={`gallery-item-${it.id}`}>
              <button
                type="button"
                onClick={() => setLightbox(it)}
                className="block w-full overflow-hidden"
                style={{ background: "#fff" }}
              >
                <img
                  src={resolveImageUrl(it.url)}
                  alt={it.caption || ""}
                  className="w-full h-auto block transition-transform duration-700 hover:scale-[1.02]"
                  loading="lazy"
                />
              </button>
              {isAdmin && (
                <button
                  onClick={() => remove(it.id)}
                  className="absolute top-2 right-2 bg-black/85 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-remove-${it.id}`}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white"
            data-testid="gallery-lightbox-close"
          >
            <X size={24} />
          </button>
          <img
            src={resolveImageUrl(lightbox.url)}
            alt={lightbox.caption || ""}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Gallery;
